/**
 * Read-only multi-roof plan viewer.
 *
 * Roof outlines come from canonical project geometry. Wind zones, pressure
 * labels and dimensions come from backend ModuleResult geometry/results. This
 * component performs presentation transforms only.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { CoordinateIndicator } from '../../shared/CoordinateIndicator';
import type { LayerVisibility } from '../roof-plan-editor/render';

import type {
  CalculationResponse, DimensionAnnotation, Project, RoofModule, WindZoneGeometry, ZoneResult,
} from '../../gen/ejot/roofing/v1/types';
import { polygonRings, polygonSvgPath } from '../../shared/polygonRings';
import { layoutDimensionsOutsideRoof, type DimensionLayout } from '../wind-zone-plan/WindZonePlan';
import { zoneEngineeringLabel } from '../wind-zone-plan/zonePresentation';
import './ProjectPlanOverview.css';

export type ProjectPlanMode = '2d' | 'zones';

interface XY { x: number; y: number }
interface Bounds { minX: number; minY: number; maxX: number; maxY: number }
interface ViewBox { minX: number; minY: number; width: number; height: number }

export interface ProjectPlanRoof {
  id: string;
  label: string;
  module: RoofModule;
  outline: XY[];
  zones: readonly WindZoneGeometry[];
  dimensions: readonly DimensionAnnotation[];
  results: readonly ZoneResult[];
}

interface RoofDrawing {
  roof: ProjectPlanRoof;
  bounds: Bounds;
  drawingSpan: number;
  dimensions: DimensionLayout[];
}

export function assembleProjectPlanRoofs(
  project: Project,
  result: CalculationResponse | undefined,
): ProjectPlanRoof[] {
  const resultByModule = new Map(
    (result?.modules ?? []).map((moduleResult) => [moduleResult.module_id ?? '', moduleResult]),
  );
  return (project.modules ?? []).flatMap((module, index) => {
    const points = module.build_up?.plan?.outline?.points ?? [];
    if (points.length < 3) return [];
    const id = module.id ?? `roof-${index + 1}`;
    const moduleResult = resultByModule.get(module.id ?? '');
    return [{
      id,
      label: module.display_name?.trim() || `Roof ${index + 1}`,
      module,
      outline: points.map((point) => ({ x: point.x ?? 0, y: point.y ?? 0 })),
      zones: moduleResult?.geometry?.wind_zones ?? [],
      dimensions: moduleResult?.geometry?.dimensions ?? [],
      results: moduleResult?.zones ?? [],
    }];
  });
}

export function ProjectPlanOverview({
  project,
  result,
  mode,
  activeModuleId,
  layers = {},
  onActiveModuleChange,
  onOpenModule,
}: {
  project: Project;
  result: CalculationResponse | undefined;
  mode: ProjectPlanMode;
  activeModuleId?: string;
  layers?: LayerVisibility;
  onActiveModuleChange?: (moduleId: string) => void;
  onOpenModule?: (moduleId: string) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const pointerRef = useRef(new Map<number, { x: number; y: number }>());
  const panRef = useRef<{ pointerId: number; x: number; y: number; view: ViewBox }>();
  const pinchRef = useRef<{ distance: number; centre: { x: number; y: number }; view: ViewBox }>();

  const roofs = useMemo(() => assembleProjectPlanRoofs(project, result), [project, result]);
  const drawings = useMemo<RoofDrawing[]>(() => roofs.map((roof) => {
    const bounds = boundsOf(roof.outline);
    const drawingSpan = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 1);
    return {
      roof,
      bounds,
      drawingSpan,
      dimensions: layoutDimensionsOutsideRoof(roof.dimensions.filter((dimension) => dimension.visible !== false), bounds, drawingSpan, roof.zones),
    };
  }), [roofs]);
  const fitted = useMemo(() => fitProjectView(drawings, mode), [drawings, mode]);
  const [view, setView] = useState<ViewBox>(fitted);
  const selected = roofs.find((roof) => roof.id === activeModuleId) ?? roofs[0];

  useEffect(() => setView(fitted), [fitted]);

  if (roofs.length === 0) {
    return (
      <div className="all-roofs-empty">
        <strong>No roof geometry is available.</strong>
        <span>Add or complete a roof module to populate the project overview.</span>
      </div>
    );
  }

  const zoom = (factor: number, screenPoint?: { x: number; y: number }) => {
    setView((current) => zoomView(current, factor, screenPoint, svgRef.current));
  };

  const onPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointerRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const pointers = [...pointerRef.current.values()];
    if (pointers.length === 1) {
      panRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, view };
      pinchRef.current = undefined;
    } else if (pointers.length >= 2) {
      panRef.current = undefined;
      const [a, b] = pointers;
      if (!a || !b) return;
      pinchRef.current = {
        distance: Math.max(Math.hypot(b.x - a.x, b.y - a.y), 1),
        centre: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        view,
      };
    }
  };

  const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!pointerRef.current.has(event.pointerId)) return;
    pointerRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const pointers = [...pointerRef.current.values()];
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return;

    if (pointers.length >= 2 && pinchRef.current) {
      const [a, b] = pointers;
      if (!a || !b) return;
      const currentDistance = Math.max(Math.hypot(b.x - a.x, b.y - a.y), 1);
      const currentCentre = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const factor = pinchRef.current.distance / currentDistance;
      const zoomed = zoomView(pinchRef.current.view, factor, pinchRef.current.centre, svgRef.current);
      const dx = currentCentre.x - pinchRef.current.centre.x;
      const dy = currentCentre.y - pinchRef.current.centre.y;
      setView({
        ...zoomed,
        minX: zoomed.minX - (dx / rect.width) * zoomed.width,
        minY: zoomed.minY - (dy / rect.height) * zoomed.height,
      });
      return;
    }

    const pan = panRef.current;
    if (!pan || pan.pointerId !== event.pointerId) return;
    const dx = event.clientX - pan.x;
    const dy = event.clientY - pan.y;
    setView({
      ...pan.view,
      minX: pan.view.minX - (dx / rect.width) * pan.view.width,
      minY: pan.view.minY - (dy / rect.height) * pan.view.height,
    });
  };

  const releasePointer = (event: React.PointerEvent<SVGSVGElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    pointerRef.current.delete(event.pointerId);
    panRef.current = undefined;
    pinchRef.current = undefined;
    const remaining = [...pointerRef.current.entries()];
    if (remaining.length === 1) {
      const [pointerId, point] = remaining[0]!;
      panRef.current = { pointerId, x: point.x, y: point.y, view };
    }
  };

  return (
    <div className="project-plan-overview" ref={hostRef}>
      <div className="project-plan-toolbar">
        <div className="project-plan-roof-buttons" role="group" aria-label="Project roof selection">
          {roofs.map((roof) => (
            <button
              key={roof.id}
              type="button"
              className={roof.id === selected?.id ? 'toolbar-button is-active' : 'toolbar-button'}
              aria-pressed={roof.id === selected?.id}
              onClick={() => onActiveModuleChange?.(roof.id)}
            >
              {roof.label}
            </button>
          ))}
        </div>
        <div className="project-plan-actions" role="group" aria-label="2D navigation controls">
          <button type="button" className="viewer-nav-button" aria-label="Zoom in" onClick={() => zoom(0.82)}>+</button>
          <button type="button" className="viewer-nav-button" aria-label="Zoom out" onClick={() => zoom(1.22)}>-</button>
          <button type="button" className="viewer-nav-button is-primary" onClick={() => setView(fitted)}>Extents</button>
          {selected && onOpenModule && (
            <button type="button" className="toolbar-button" onClick={() => onOpenModule(selected.id)}>Open {selected.label}</button>
          )}
        </div>
      </div>

      <div className="project-plan-stage">
        <svg
          ref={svgRef}
          className="project-plan-svg"
          viewBox={`${view.minX} ${view.minY} ${view.width} ${view.height}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={`${mode === 'zones' ? 'All Roofs 2D Zones' : 'All Roofs 2D View'} with interactive pan and zoom`}
          onWheel={(event) => {
            event.preventDefault();
            zoom(event.deltaY > 0 ? 1.12 : 0.88, { x: event.clientX, y: event.clientY });
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={releasePointer}
          onPointerCancel={releasePointer}
        >
          <defs>
            <marker id="project-plan-arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto-start-reverse" markerUnits="strokeWidth">
              <path d="M 3 0 L 3 6" fill="none" stroke="var(--ejot-red)" strokeWidth="1.2" />
            </marker>
          </defs>

          {drawings.map(({ roof, dimensions, drawingSpan }) => {
            const selectedRoof = roof.id === selected?.id;
            const resultByZone = new Map(roof.results.map((zoneResult) => [zoneResult.zone_id ?? '', zoneResult]));
            const centre = centroid(roof.outline);
            return (
              <g key={roof.id} className={selectedRoof ? 'project-plan-roof is-selected' : 'project-plan-roof'}>
                {mode === 'zones' && roof.zones.map((zone) => {
                  const path = polygonSvgPath(zone.outline);
                  if (!path) return null;
                  return (
                    <path
                      key={zone.id}
                      d={path}
                      fill={semanticZoneColour(zone.colour_token)}
                      fillRule="evenodd"
                      clipRule="evenodd"
                      fillOpacity={0.58}
                      stroke={semanticZoneColour(zone.colour_token)}
                      strokeWidth={1.2}
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })}

                <path
                  d={outlinePath(roof.outline)}
                  fill={mode === 'zones' ? 'none' : 'var(--surface-muted)'}
                  stroke={selectedRoof ? 'var(--ejot-red)' : 'var(--text-primary)'}
                  strokeWidth={selectedRoof ? 2.6 : 1.6}
                  vectorEffect="non-scaling-stroke"
                  onClick={(event) => {
                    event.stopPropagation();
                    onActiveModuleChange?.(roof.id);
                  }}
                  onDoubleClick={(event) => {
                    event.stopPropagation();
                    onOpenModule?.(roof.id);
                  }}
                />

                {mode === 'zones' && roof.zones.map((zone) => {
                  if (!zone.label_anchor) return null;
                  const label = zoneEngineeringLabel(zone, resultByZone.get(zone.id ?? ''));
                  const x = zone.label_anchor.x ?? 0;
                  const y = zone.label_anchor.y ?? 0;
                  return (
                    <g key={`label-${roof.id}-${zone.id}`} pointerEvents="none">
                      <text
                        x={x}
                        y={y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="var(--text-primary)"
                        fontFamily="Roboto Condensed, Arial, sans-serif"
                        fontSize={drawingSpan * 0.018}
                        fontWeight={700}
                        paintOrder="stroke"
                        stroke="var(--surface-card)"
                        strokeWidth={drawingSpan * 0.0025}
                      >
                        {label.zoneText}
                      </text>
                      {label.pressureText && (
                        <text
                          x={x}
                          y={y + drawingSpan * 0.022}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="var(--text-secondary)"
                          fontFamily="Roboto Condensed, Arial, sans-serif"
                          fontSize={drawingSpan * 0.011}
                          fontWeight={600}
                          paintOrder="stroke"
                          stroke="var(--surface-card)"
                          strokeWidth={drawingSpan * 0.0025}
                        >
                          {label.pressureText}
                        </text>
                      )}
                    </g>
                  );
                })}

                {dimensions.map((layout) => <ProjectDimension key={`${roof.id}-${layout.dimension.id}`} layout={layout} drawingSpan={drawingSpan} />)}

                <text
                  x={centre.x}
                  y={centre.y - drawingSpan * 0.035}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="var(--text-primary)"
                  fontFamily="Roboto Condensed, Arial, sans-serif"
                  fontSize={drawingSpan * 0.016}
                  fontWeight={800}
                  paintOrder="stroke"
                  stroke="var(--surface-card)"
                  strokeWidth={drawingSpan * 0.003}
                  pointerEvents="none"
                >
                  {roof.label}
                </text>
              </g>
            );
          })}
        </svg>
        <CoordinateIndicator mode="2d" visible={layers.axes !== false} />
      </div>

      <div className="project-plan-status">
        <span>{roofs.length} roof{roofs.length === 1 ? '' : 's'}</span>
        <span>Drag to pan · wheel/pinch to zoom · Extents to fit</span>
        <span>Read only</span>
      </div>
    </div>
  );
}

function ProjectDimension({ layout, drawingSpan }: { layout: DimensionLayout; drawingSpan: number }) {
  return (
    <g data-dimension-id={layout.dimension.id} pointerEvents="none">
      <line x1={layout.sourceAX} y1={layout.sourceAY} x2={layout.ax} y2={layout.ay} stroke="var(--ejot-red)" strokeOpacity={0.65} strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
      <line x1={layout.sourceBX} y1={layout.sourceBY} x2={layout.bx} y2={layout.by} stroke="var(--ejot-red)" strokeOpacity={0.65} strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
      <line x1={layout.ax} y1={layout.ay} x2={layout.bx} y2={layout.by} stroke="var(--ejot-red)" strokeWidth={1.2} vectorEffect="non-scaling-stroke" markerStart="url(#project-plan-arrow)" markerEnd="url(#project-plan-arrow)" />
      <text
        x={layout.textX}
        y={layout.textY}
        textAnchor="middle"
        dominantBaseline="middle"
        transform={layout.textRotationDegrees === 0 ? undefined : `rotate(${layout.textRotationDegrees} ${layout.textX} ${layout.textY})`}
        fill="var(--ejot-red)"
        fontFamily="Roboto Condensed, Arial, sans-serif"
        fontSize={drawingSpan * 0.011}
        fontWeight={600}
        paintOrder="stroke"
        stroke="var(--surface-card)"
        strokeWidth={drawingSpan * 0.004}
      >
        {layout.label}
      </text>
    </g>
  );
}

function boundsOf(points: readonly XY[]): Bounds {
  if (points.length === 0) return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  return {
    minX: Math.min(...points.map((point) => point.x)),
    minY: Math.min(...points.map((point) => point.y)),
    maxX: Math.max(...points.map((point) => point.x)),
    maxY: Math.max(...points.map((point) => point.y)),
  };
}

function fitProjectView(drawings: readonly RoofDrawing[], mode: ProjectPlanMode): ViewBox {
  const points: XY[] = [];
  for (const drawing of drawings) {
    points.push(...drawing.roof.outline);
    if (mode === 'zones') {
      for (const zone of drawing.roof.zones) {
        for (const ring of polygonRings(zone.outline)) {
          points.push(...ring.map((point) => ({ x: point.x ?? 0, y: point.y ?? 0 })));
        }
      }
    }
    for (const dimension of drawing.dimensions) {
      points.push(
        { x: dimension.ax, y: dimension.ay },
        { x: dimension.bx, y: dimension.by },
        { x: dimension.textX, y: dimension.textY },
      );
    }
  }
  const bounds = boundsOf(points);
  const spanX = Math.max(bounds.maxX - bounds.minX, 1);
  const spanY = Math.max(bounds.maxY - bounds.minY, 1);
  const span = Math.max(spanX, spanY, 1000);
  const padding = Math.max(800, span * 0.08);
  return {
    minX: bounds.minX - padding,
    minY: bounds.minY - padding,
    width: spanX + 2 * padding,
    height: spanY + 2 * padding,
  };
}

function zoomView(
  current: ViewBox,
  factor: number,
  screenPoint: { x: number; y: number } | undefined,
  svg: SVGSVGElement | null,
): ViewBox {
  if (!Number.isFinite(factor) || factor <= 0) return current;
  const nextWidth = Math.max(current.width * factor, 10);
  const nextHeight = Math.max(current.height * factor, 10);
  const rect = svg?.getBoundingClientRect();
  const ratioX = rect && screenPoint ? clamp((screenPoint.x - rect.left) / Math.max(rect.width, 1), 0, 1) : 0.5;
  const ratioY = rect && screenPoint ? clamp((screenPoint.y - rect.top) / Math.max(rect.height, 1), 0, 1) : 0.5;
  const anchorX = current.minX + current.width * ratioX;
  const anchorY = current.minY + current.height * ratioY;
  return {
    minX: anchorX - nextWidth * ratioX,
    minY: anchorY - nextHeight * ratioY,
    width: nextWidth,
    height: nextHeight,
  };
}

function outlinePath(points: readonly XY[]): string {
  if (points.length < 3) return '';
  return `${points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ')} Z`;
}

function centroid(points: readonly XY[]): XY {
  if (points.length === 0) return { x: 0, y: 0 };
  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
  };
}

function semanticZoneColour(token: string | undefined): string {
  if (!token || !/^zone-[a-z0-9-]+$/.test(token)) return 'var(--zone-interior)';
  return `var(--${token})`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
